import {Avatar, Box, MenuItem, Select, Switch, Typography, useTheme} from '@mui/material';
import {Page_Participation_, Participation, Theme} from '../../types';
import {
    fetchCampaignParticipation,
    useGetCampaignParticipation,
} from '../../queries/campaignQueries';
import {useEffect, useState} from 'react';

import {Colors} from '../common/colors';
import {CustomButton} from '../common/button';
import {PARTICIPATIONS_PAGE_SIZE} from '../../utils/settings';
import {getPictureFromUrl} from '../../utils/user';
import {useResponsive} from '../../provider/MediaQueryProvider';
import {useTranslation} from 'next-i18next';

type ParticipationListProps = {
    campaignId: string;
    theme: Theme;
    initialData?: Page_Participation_;
};

export default function ParticipationList(props: ParticipationListProps) {
    // const theme = useTheme();
    // const isDark = theme.palette.mode == 'dark';
    const {t} = useTranslation('common');
    const {isMobile, isTablet} = useResponsive();
    const [order, setOrder] = useState<string>('DESC');
    const [page, setPage] = useState<number>(1);
    const [total, setTotal] = useState<number>(0);
    const [commentOnly, setCommentOnly] = useState<boolean>(true);
    const [finished, setFinished] = useState<boolean>(false);
    const [participations, setParticipations] = useState<Participation[]>([]);

    const {data} = useGetCampaignParticipation(
        props.campaignId,
        1,
        PARTICIPATIONS_PAGE_SIZE,
        Boolean(props.campaignId),
        props.initialData
    );

    useEffect(() => {
        if (data?.items) {
            setParticipations(data.items);
            setTotal(data.total);
        }
    }, [data]);

    useEffect(() => {
        // console.log('participations.length = ', participations.length);
        // console.log('total = ', total);
        setFinished(participations.length == total);
    }, [participations, total]);

    async function toggleCommentOnly() {
        const newCommentOnly = !commentOnly;
        setCommentOnly(newCommentOnly);
        const newPage = 1;

        const data = await fetchCampaignParticipation(
            props.campaignId,
            order,
            newCommentOnly,
            newPage,
            PARTICIPATIONS_PAGE_SIZE
        );

        setParticipations(data.items);
        setTotal(data.total);
        setPage(newPage);
    }

    async function changeOrder(newOrder: string) {
        // console.log('changeOrder = ', newOrder);
        setOrder(newOrder);
        const newPage = 1;

        const data = await fetchCampaignParticipation(
            props.campaignId,
            newOrder,
            commentOnly,
            newPage,
            PARTICIPATIONS_PAGE_SIZE
        );

        setParticipations(data.items);
        setTotal(data.total);
        setPage(newPage);
    }

    async function loadMore() {
        const newPage = page + 1;
        setPage(newPage);

        const data = await fetchCampaignParticipation(
            props.campaignId,
            order,
            commentOnly,
            newPage,
            PARTICIPATIONS_PAGE_SIZE
        );

        const newParticipations = [...participations, ...data.items] as Participation[];
        setParticipations(newParticipations);
        setTotal(data.total);
    }

    // console.log('ORDER = ', order);
    // console.log('PAGE = ', page);
    // console.log('PARTICIPATIONS = ', participations);
    // console.log('FINISHED = ', finished);

    const flexDirection = isTablet || isMobile ? 'column' : 'row';
    const margin = isTablet || isMobile ? '25px' : '50px';

    const bgColor = props.theme?.background_color ?? Colors.first;

    return (
        <>
            {total > 1 && (
                <Box
                    display="flex"
                    flexDirection="row"
                    justifyContent="center"
                    marginTop={margin}
                    marginBottom={margin}
                >
                    <Typography
                        color={bgColor}
                        textTransform="uppercase"
                        alignSelf="center"
                        fontWeight="bold"
                        fontSize="18px"
                        textAlign="center"
                    >
                        {t('label.thank_to')} {total} {t('campaign.participants')}
                    </Typography>
                </Box>
            )}
            <Box
                display="flex"
                flexDirection={flexDirection}
                marginBottom={isTablet || isMobile ? '25px' : '50px'}
                marginTop={isTablet || isMobile ? '25px' : '50px'}
            >
                <Box display="flex" flexDirection="row">
                    <Typography alignSelf="center" marginRight="10px">
                        {t('label.order_by')}
                    </Typography>
                    <Select
                        value={order}
                        onChange={(event) => changeOrder(event.target.value)}
                        sx={{
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: bgColor,
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: bgColor,
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: bgColor,
                            },
                        }}
                    >
                        <MenuItem value={'DESC'} key={'DESC'}>
                            {t('label.most_recent')}
                        </MenuItem>
                        <MenuItem value={'ASC'} key={'ASC'}>
                            {t('label.most_old')}
                        </MenuItem>
                    </Select>
                </Box>
                <Box
                    display="flex"
                    flexDirection="row"
                    marginLeft={isTablet || isMobile ? '0px' : '20px'}
                >
                    <Switch
                        checked={commentOnly}
                        onClick={toggleCommentOnly}
                        sx={{
                            alignSelf: 'center',
                            '& .MuiSwitch-track': {
                                backgroundColor: Colors.grey,
                            },
                            '& .Mui-checked+.MuiSwitch-track': {
                                backgroundColor: props.theme.hover_color,
                            },
                            '& .MuiSwitch-thumb': {
                                backgroundColor: Colors.grey,
                            },
                            '& .Mui-checked .MuiSwitch-thumb': {
                                backgroundColor: props.theme.hover_color,
                            },
                        }}
                    />
                    <Typography alignSelf="center">{t('action.only_comment')}</Typography>
                </Box>
            </Box>
            <Box>
                {participations?.map((p) => (
                    <ParticipationElement
                        participation={p}
                        theme={props.theme}
                        key={p.created}
                    />
                ))}
            </Box>
            {!finished && (
                <Box display="flex" flexDirection="row" justifyContent="center">
                    <CustomButton
                        sx={{
                            marginTop: '20px',
                            color: 'primary',
                            borderColor: bgColor,
                            backgroundColor: 'transparent',
                            '&:hover': {
                                backgroundColor: bgColor,
                            },
                        }}
                        onClick={() => loadMore()}
                    >
                        {t('action.more')}
                    </CustomButton>
                </Box>
            )}
        </>
    );
}

type ParticipationElementProps = {
    participation: Participation;
    theme?: Theme | null | undefined;
};

function ParticipationElement(props: ParticipationElementProps) {
    const theme = useTheme();
    const isDark = theme.palette.mode == 'dark';
    const {t} = useTranslation('common');
    const {isMobile, isTablet} = useResponsive();
    const participation = props.participation;
    const amount = participation.amount;
    const date = new Date(Date.parse(participation.created));

    const flexDirection = isTablet || isMobile ? 'column' : 'row';

    return (
        <Box
            sx={{
                WebkitBoxShadow: Colors.shadow,
                boxShadow: Colors.shadow,
                borderRadius: '8px',
                marginBottom: '30px',
                padding: '20px',
                backgroundColor: isDark ? Colors.dark : 'none',
                maxWidth: '853px',
                marginLeft: 'auto',
                marginRight: 'auto',
            }}
        >
            <Box display="flex" flexDirection="row" justifyContent="space-between">
                <Box display="flex" flexDirection={flexDirection} justifyContent="center">
                    <Box display="flex" flexDirection="row" justifyContent="space-between">
                        <ParticipationIdentity participation={participation} />
                        {isMobile && (
                            <Typography fontSize="10px" alignSelf="center">
                                {date.toLocaleDateString()}
                            </Typography>
                        )}
                    </Box>
                    <Box display="flex" flexDirection="row">
                        <Typography component="span">{t('campaign.participated')} </Typography>
                        <Typography
                            component="span"
                            sx={{
                                fontWeight: 'bold',
                                color: props.theme?.background_color ?? Colors.second,
                                display: 'inline-block',
                                marginLeft: '4px',
                            }}
                        >
                            {amount} €
                        </Typography>
                    </Box>
                </Box>
                {!isMobile && (
                    <Box>
                        <Typography>{date.toLocaleDateString()}</Typography>
                    </Box>
                )}
            </Box>
            {Boolean(participation.comment) && (
                <Typography
                    alignSelf={'center'}
                    sx={{
                        backgroundColor: isDark ? 'none' : Colors.light,
                        borderWidth: '1px',
                        borderStyle: 'solid',
                        borderColor: isDark ? Colors.grey : Colors.light,
                        borderRadius: '5px',
                        padding: '5px',
                        marginTop: '5px',
                        marginBottom: '5px',
                    }}
                >
                    {participation.comment} {participation.emoji}
                </Typography>
            )}
        </Box>
    );
}

type ParticipationIdentityProps = {
    participation: Participation;
    overrideName?: string | undefined;
};

export function ParticipationIdentity(props: ParticipationIdentityProps) {
    // console.log('participation=', props.participation);
    const participation = props.participation;
    const userName = props.overrideName ? props.overrideName : participation.name;
    const name = participation.anonymous ? 'Anonyme' : userName;
    const url = participation.anonymous ? null : participation.user?.picture_url;
    const picture = getPictureFromUrl(url);
    return (
        <Box display="flex" flexDirection="row">
            <Avatar
                alt={name}
                src={picture}
                imgProps={{referrerPolicy: 'no-referrer'}}
                sx={{width: '30px', height: '30px'}}
            />
            <Typography sx={{marginLeft: '10px', minWidth: '200px', alignSelf: 'center'}}>
                {name}
            </Typography>
        </Box>
    );
}
